import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SpinnerComponent, SpinnerConfig } from '../spinner/spinner.component';
import { CompanyContextService } from '@core/auth/services/company-context.service';

/**
 * A single global loading state: a keyword, the condition signal that turns it on,
 * and the message to show while it's active.
 */
interface GlobalLoaderCase {
  /** Stable keyword for this loader (e.g. 'companySwitching'). */
  key: string;
  /** Truthy → this loader is active. Reads signals, so it stays reactive. */
  active: () => boolean;
  /** Message shown in the loader card while active. */
  message: string;
}

/**
 * Full-screen blocking loader overlay.
 *
 * Self-contained: it owns a registry of global loading `cases` (keyword →
 * condition + message). When any case's condition is true, the overlay shows with
 * that case's message. To add a new global loader in the future, add one entry to
 * `cases` — no inputs or wiring at the call site needed (`<app-global-loader />`).
 **/
@Component({
  selector: 'app-global-loader',
  standalone: true,
  imports: [SpinnerComponent],
  template: `
    @if (show()) {
      <div class="global-loader-overlay" role="status" aria-live="polite">
        <div class="global-loader-card">
          <app-spinner [config]="spinnerConfig()" />
          <span>{{ message() }}</span>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .global-loader-overlay {
        position: fixed;
        inset: 0;
        z-index: 1100;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(15, 23, 42, 0.35);
        backdrop-filter: blur(1px);
      }
      .global-loader-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 22px 28px;
        border-radius: 12px;
        background: var(--surface-card, #fff);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
        color: var(--fg-default, #1e293b);
        font-size: 14px;
        font-weight: 500;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalLoaderComponent {
  readonly spinnerConfig = input<SpinnerConfig>({ type: 'infinite-spinner', size: 'medium' });
  private readonly companyContext = inject(CompanyContextService);
  private readonly cases: GlobalLoaderCase[] = [
    {
      key: 'companySwitching',
      active: () => this.companyContext.switching(),
      message: 'Switching company…',
    },
  ];

  /** The first active case (cases are checked in order), or null when idle. */
  private readonly activeCase = computed(() => this.cases.find((c) => c.active()) ?? null);

  readonly show = computed(() => this.activeCase() !== null);
  readonly message = computed(() => this.activeCase()?.message ?? 'Loading…');
}
